<script setup lang="ts">
const props = defineProps<{
    navigationId: string;
}>();

const route = useRoute()
const { search } = useProductSearch()
const { apiClient } = useShopwareContext()
const runtimeConfig = useRuntimeConfig()
const baseUrl = runtimeConfig.public.shopware?.devStorefrontUrl || useRequestURL().origin

const { data: productResponse } = await useAsyncData(
    'cmsProduct' + props.navigationId,
    async () => {
        return await search(props.navigationId, {
            withCmsAssociations: true,
            associations: {
                manufacturer: {
                    associations: {
                        media: {}
                    }
                },
                media: {
                    associations: {
                        media: {}
                    }
                },
                cover: {
                    associations: {
                        media: {}
                    }
                },
                crossSellings: {},
                categories: {},
                properties: {
                    associations: {
                        group: {}
                    }
                }
            },
            criteria: {
                includes: {
                    product: [
                        'id',
                        'name',
                        'translated',
                        'productNumber',
                        'manufacturerId',
                        'manufacturer',
                        'cover',
                        'media',
                        'calculatedPrice',
                        'calculatedPrices',
                        'calculatedCheapestPrice',
                        'calculatedMaxPurchase',
                        'price',
                        'prices',
                        'stock',
                        'active',
                        'availableStock',
                        'available',
                        'isCloseout',
                        'isNew',
                        'minPurchase',
                        'maxPurchase',
                        'purchaseSteps',
                        'childCount',
                        'cmsPage',
                        'crossSellings',
                        'categories',
                        'seoCategory',
                        'shippingFree',
                        'properties',
                        'options',
                        'optionIds',
                        'customFields',
                        'seoUrls',
                        'metaTitle',
                        'metaDescription',
                        'keywords',
                        'parentId',
                        'downloads',
                        'markAsTopseller',
                        'breadcrumb'
                    ],
                    product_cross_selling: [
                        'id',
                        'name',
                        'translated',
                        'assignedProducts'
                    ],
                    product_cross_selling_assigned_products: [
                        'id',
                        'product',
                        'productId'
                    ],
                    seo_url: [
                        'seoPathInfo',
                        'pathInfo'
                    ]
                }
            }
        })
    },
)

if (!productResponse.value) {
    throw createError({ statusCode: 404, message: 'Product not found' })
}

const { product } = useProduct(
    productResponse.value.product,
    productResponse.value.configurator,
)

const rawBreadcrumbs = (product.value as unknown as { breadcrumb?: Array<{ id: string, name: string, seoUrl: string | null }> }).breadcrumb ?? []
const breadcrumbItems = [
    ...rawBreadcrumbs.map(item => ({ name: item.name, path: item.seoUrl ?? undefined })),
    { name: product.value.translated?.name ?? product.value.name ?? '', path: route.path }
]
useBreadcrumbs(breadcrumbItems)

type ProductWithSeo = typeof product.value & {
    parentId?: string
    seoUrls?: Array<{ seoPathInfo?: string }>
}

const parentId = (product.value as ProductWithSeo).parentId

const { data: parentSeoData } = parentId
    ? await useAsyncData(`parentProductSeo-${parentId}`, () =>
        apiClient.invoke('readProduct post /product', {
            headers: { 'sw-include-seo-urls': true },
            body: {
                filter: [{ type: 'equals', field: 'id', value: parentId }],
                limit: 1,
                includes: {
                    product: ['id', 'seoUrls'],
                    seo_url: ['seoPathInfo'],
                },
            },
        }).then(r => r.data?.elements?.[0] ?? null)
    )
    : { data: ref(null) }

const canonicalUrl = computed(() => {
    const p = product.value as ProductWithSeo
    const parentSeoPath = (parentSeoData.value as { seoUrls?: Array<{ seoPathInfo?: string }> } | null)
        ?.seoUrls?.[0]?.seoPathInfo
    const seoPath = parentSeoPath ?? p.seoUrls?.[0]?.seoPathInfo
    return seoPath ? `${baseUrl}/${seoPath}` : baseUrl + route.path
})

useHead({
    link: [{ rel: 'canonical', href: canonicalUrl }]
})
</script>

<template>
    <div class="flex flex-col pt-4">
        <LayoutBreadcrumb class="!mb-0 mt-4 px-2" />
        <StructurePage v-if="product.cmsPage" :content="product.cmsPage" />
    </div>
</template>
